import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

// Fade + rise. Reliable on mobile: animates when scrolled into view, but any
// element already at/above the fold on mount is shown immediately (avoids the
// framer whileInView + smooth-scroll race that could leave content stuck hidden).
// Pass `immediate` to always animate on mount.
export const Reveal = ({ children, delay = 0, y = 40, className = "", testid, immediate = false }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });
  const [mountVisible, setMountVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // If it's within (or above) the initial viewport on mount, reveal right away.
    if (rect.top < window.innerHeight * 0.92) setMountVisible(true);
  }, []);

  const show = immediate || inView || mountVisible;

  return (
    <motion.div
      ref={ref}
      className={className}
      data-testid={testid}
      initial={{ opacity: 0, y }}
      animate={show ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

// Masked line-by-line reveal. `lines` is an array of strings.
export const LineReveal = ({
  lines,
  className = "",
  lineClassName = "",
  animate = "onView",
  baseDelay = 0,
  stagger = 0.12,
}) => {
  const parent =
    animate === "immediate"
      ? { initial: "hidden", animate: "show" }
      : { initial: "hidden", whileInView: "show", viewport: { once: true, margin: "-10%" } };

  return (
    <span className={className} aria-label={lines.join(" ")}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.18em] -mb-[0.18em]" aria-hidden="true">
          <motion.span
            className={`block ${lineClassName}`}
            {...parent}
            variants={{
              hidden: { y: "115%" },
              show: {
                y: "0%",
                transition: { duration: 1, delay: baseDelay + i * stagger, ease: EASE },
              },
            }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
};
